import React, { useEffect, useMemo, useState } from 'react';
import api from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import CurrencyDisplay from '../components/CurrencyDisplay';
import {
  Building2, Calculator, TrendingDown, TrendingUp, AlertTriangle,
  CheckCircle2, Receipt, Calendar, RefreshCw,
} from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_META = {
  profit:    { label: 'ربح',  color: 'emerald-400', bg: 'emerald-500/10', border: 'emerald-500/40', icon: CheckCircle2 },
  breakeven: { label: 'تعادل', color: 'amber-400',   bg: 'amber-500/10',   border: 'amber-500/40',   icon: TrendingUp },
  loss:      { label: 'خسارة', color: 'red-400',     bg: 'red-500/10',     border: 'red-500/40',     icon: TrendingDown },
};

const RentSimulator = () => {
  const { isAdmin } = useAuth();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [scenarios, setScenarios] = useState([
    { label: 'سيناريو 1', annual_rent: 48000 },
    { label: 'سيناريو 2', annual_rent: 60000 },
    { label: 'سيناريو 3', annual_rent: 72000 },
    { label: 'سيناريو 4', annual_rent: 90000 },
  ]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const compute = async () => {
    const valid = scenarios.filter(s => (s.label || '').trim() && Number(s.annual_rent) > 0);
    if (valid.length === 0) {
      toast.error('أدخل قيمة إيجار سنوي واحدة على الأقل');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/salaries/rent-simulator/compute', { month, scenarios: valid });
      setResult(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الحساب');
    } finally {
      setLoading(false);
    }
  };

  // Compute once on mount
  useEffect(() => { compute(); /* eslint-disable-next-line */ }, []);

  const bestScenario = useMemo(() => {
    if (!result?.scenarios?.length) return null;
    return result.scenarios.reduce((best, s) => (s.net_profit > (best?.net_profit ?? -Infinity) ? s : best), null);
  }, [result]);

  if (!isAdmin()) {
    return <div className="text-center py-12 text-muted-foreground">ليس لديك صلاحية للوصول إلى هذه الصفحة</div>;
  }

  const updateScenario = (i, patch) => {
    setScenarios((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  };

  return (
    <div className="space-y-6" data-testid="rent-simulator-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-primary flex items-center gap-2">
            <Building2 className="w-7 h-7" />
            محاكاة الإيجار السنوي
          </h1>
          <p className="text-muted-foreground text-sm">
            ادخل 4 أنماط للإيجار السنوي ويحسب النظام جدوى الصالون لكل نمط بناءً على أداء الشهر الحالي مع الإبقاء على فواتيرك الحالية (نت/كهرباء/ماء) كما هي.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">الشهر المرجعي</label>
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="bg-secondary/50 border-white/10 w-44"
              data-testid="rent-sim-month"
            />
          </div>
          <Button onClick={compute} disabled={loading} className="bg-primary text-primary-foreground gap-2" data-testid="rent-sim-compute-btn">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
            احسب
          </Button>
        </div>
      </div>

      {/* Scenario inputs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {scenarios.map((s, i) => (
          <div key={i} className="bg-card border border-white/10 rounded-xl p-3" data-testid={`rent-sim-scenario-${i}`}>
            <Input
              value={s.label}
              onChange={(e) => updateScenario(i, { label: e.target.value })}
              placeholder="اسم السيناريو"
              className="mb-2 bg-secondary/50 text-sm"
            />
            <label className="text-xs text-muted-foreground">إيجار سنوي (د.إ)</label>
            <Input
              type="number"
              min={0}
              step={1000}
              value={s.annual_rent}
              onChange={(e) => updateScenario(i, { annual_rent: Number(e.target.value) || 0 })}
              className="bg-secondary/50 text-lg font-bold"
            />
            <div className="mt-2 text-[11px] text-muted-foreground">
              شهرياً ≈ <strong className="text-primary">{fmt(s.annual_rent / 12)}</strong> د.إ
            </div>
          </div>
        ))}
      </div>

      {/* Current month base */}
      {result && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiBox icon={Calendar} label="الشهر" value={result.month} accent="primary" />
          <KpiBox icon={TrendingUp} label="إيراد الصالون" value={<CurrencyDisplay amount={result.salon_revenue} />} accent="primary" />
          <KpiBox icon={Receipt} label="الرواتب المدفوعة" value={<CurrencyDisplay amount={result.salon_salaries_paid} className="text-red-400" />} accent="red-400" />
          <KpiBox icon={Building2} label="الفواتير الحالية" value={<CurrencyDisplay amount={result.monthly_bills?.total} className="text-amber-400" />} accent="amber-400" sub={`${result.monthly_bills?.items?.length || 0} عناصر`} />
        </div>
      )}

      {/* Bills detail */}
      {result?.monthly_bills?.items?.length > 0 && (
        <div className="bg-card border border-white/10 rounded-xl p-4">
          <div className="text-sm font-bold mb-2 text-muted-foreground">الفواتير الحالية (تبقى كما هي في جميع السيناريوهات)</div>
          <div className="flex flex-wrap gap-2 text-xs">
            {result.monthly_bills.items.map((b) => (
              <span key={b.name} className="bg-secondary/50 border border-white/10 rounded-md px-2 py-1">
                {b.name}: <strong className="text-amber-400">{fmt(b.amount)} د.إ</strong>
              </span>
            ))}
            {result.other_annual_monthly_share > 0 && (
              <span className="bg-secondary/50 border border-white/10 rounded-md px-2 py-1">
                حصة شهرية لالتزامات سنوية أخرى: <strong className="text-blue-400">{fmt(result.other_annual_monthly_share)} د.إ</strong>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Scenario results */}
      {result && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {result.scenarios.map((sc, i) => {
            const meta = STATUS_META[sc.status] || STATUS_META.loss;
            const Icon = meta.icon;
            const isBest = bestScenario && bestScenario.label === sc.label && sc.net_profit > 0;
            return (
              <div
                key={sc.label || `result-${i}`}
                className={`bg-card rounded-xl p-4 border relative overflow-hidden bg-${meta.bg} border-${meta.border}`}
                style={{
                  background: sc.status === 'profit' ? 'rgba(16,185,129,0.05)' : sc.status === 'breakeven' ? 'rgba(245,158,11,0.05)' : 'rgba(239,68,68,0.05)',
                  borderColor: sc.status === 'profit' ? 'rgba(16,185,129,0.4)' : sc.status === 'breakeven' ? 'rgba(245,158,11,0.4)' : 'rgba(239,68,68,0.4)',
                }}
                data-testid={`rent-sim-result-${i}`}
              >
                {isBest && (
                  <div className="absolute top-2 start-2 bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    الأفضل 🏆
                  </div>
                )}
                <div className="flex items-center justify-between mb-3">
                  <div className="font-bold text-lg">{sc.label}</div>
                  <span className={`inline-flex items-center gap-1 text-xs font-bold text-${meta.color}`}
                    style={{ color: sc.status === 'profit' ? '#10b981' : sc.status === 'breakeven' ? '#f59e0b' : '#ef4444' }}>
                    <Icon className="w-3.5 h-3.5" />
                    {meta.label}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                  <Row label="إيجار سنوي" value={`${fmt(sc.annual_rent)} د.إ`} />
                  <Row label="إيجار شهري" value={`${fmt(sc.monthly_rent)} د.إ`} />
                  <Row label="الفواتير" value={`${fmt(sc.monthly_bills_total)} د.إ`} />
                  {sc.other_monthly_share > 0 && <Row label="حصص سنوية" value={`${fmt(sc.other_monthly_share)} د.إ`} />}
                  <Row label="إجمالي تكلفة ثابتة" value={`${fmt(sc.total_fixed_cost)} د.إ`} highlight />
                  <Row label="ربح إجمالي (إيراد − رواتب)" value={`${fmt(sc.gross_profit_before_fixed)} د.إ`} />
                  <Row label="نسبة التغطية" value={`${sc.coverage_pct}%`} />
                </div>
                <div className="border-t border-white/10 pt-3 flex items-center justify-between">
                  <span className="text-sm font-bold">صافي الربح/الخسارة</span>
                  <span
                    className="text-xl font-bold"
                    style={{ color: sc.net_profit > 0 ? '#10b981' : sc.net_profit === 0 ? '#f59e0b' : '#ef4444' }}
                    data-testid={`rent-sim-net-${i}`}
                  >
                    {sc.net_profit > 0 ? '+' : ''}{fmt(sc.net_profit)} د.إ
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">{sc.verdict}</p>
              </div>
            );
          })}
        </div>
      )}

      {result && bestScenario && bestScenario.net_profit > 0 && (
        <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-4 text-sm">
          <div className="flex items-center gap-2 text-emerald-300 font-bold mb-1">
            <CheckCircle2 className="w-4 h-4" />
            توصية النظام
          </div>
          <p>أفضل سيناريو لهذا الشهر هو <strong>{bestScenario.label}</strong> بإيجار سنوي <strong>{fmt(bestScenario.annual_rent)}</strong> د.إ، يحقق صافي ربح <strong>{fmt(bestScenario.net_profit)}</strong> د.إ.</p>
        </div>
      )}

      {result && bestScenario && bestScenario.net_profit <= 0 && (
        <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-4 text-sm">
          <div className="flex items-center gap-2 text-red-300 font-bold mb-1">
            <AlertTriangle className="w-4 h-4" />
            تحذير
          </div>
          <p>أداء الصالون لهذا الشهر لا يكفي لتغطية أي من السيناريوهات الأربعة. ركّز على زيادة الإيراد أو تقليل التكاليف قبل التفكير في عقد إيجار جديد.</p>
        </div>
      )}
    </div>
  );
};

const KpiBox = ({ icon: Icon, label, value, accent = 'primary', sub }) => (
  <div className="bg-card border border-white/10 rounded-xl p-3">
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <Icon className={`w-4 h-4 text-${accent}`} />
    </div>
    <div className="text-lg font-bold">{value}</div>
    {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

const Row = ({ label, value, highlight }) => (
  <div className={`flex justify-between gap-2 ${highlight ? 'font-bold text-primary' : ''}`}>
    <span className="text-muted-foreground">{label}</span>
    <span className="font-mono" dir="ltr">{value}</span>
  </div>
);

export default RentSimulator;
